class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    console.log('[PredictiveSearch] init ✅ VERSION 2026-01-15', this);

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  // prüft, ob Eingabe wie eine Artikelnummer aussieht (z.B. 85.995.25)
  isLikelyArticleNumber(term) {
    const value = term.trim();
    const dotMatches = value.match(/\./g) || [];
    const digitMatches = value.match(/\d/g) || [];

    const dotCount = dotMatches.length;
    const digitCount = digitMatches.length;

    return dotCount >= 2 && digitCount >= 5;
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    console.log('[PredictiveSearch] onChange', searchTerm);

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    // NEU: Artikelnummern direkt im Frontend abfangen
    if (this.isLikelyArticleNumber(searchTerm)) {
      const resultsMarkup = `
        <div class="predictive-search__no-results-message">
          <p>Für Artikelnummern wie <strong>${searchTerm}</strong> werden hier keine Vorschläge angezeigt.</p>
          <p>Bitte drücke die Eingabetaste, um die Suche auszuführen.</p>
        </div>
      `;
      this.renderSearchResults(resultsMarkup);
      return;
    }

    // sonst: normale Predictive Search
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      // beim Fokus ebenfalls Artikelnummern abfangen
      if (this.isLikelyArticleNumber(searchTerm)) {
        const resultsMarkup = `
          <div class="predictive-search__no-results-message">
            <p>Für Artikelnummern wie <strong>${searchTerm}</strong> werden hier keine Vorschläge angezeigt.</p>
            <p>Bitte drücke die Eingabetaste, um die Suche auszuführen.</p>
          </div>
        `;
        this.renderSearchResults(resultsMarkup);
        return;
      }

      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}`
      + `&${encodeURIComponent('resources[type]')}=product`
      + `&${encodeURIComponent('resources[limit]')}=10`
      + `&section_id=predictive-search`
      + `&${encodeURIComponent('resources[options][unavailable_products]')}=last`
      + `&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent('title,variants.sku,tag')}`
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = section ? section.innerHTML : '';

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // NEU: Preise in den Suchvorschlägen formatieren: "€ 56,70" -> "56,70 €"
  formatPredictivePrices() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults
      .querySelectorAll('.predictive-search__item-price')
      .forEach((el) => {
        let t = (el.textContent || '').trim();

        // NBSP -> normales Leerzeichen (kommt bei Money-Strings häufig vor)
        t = t.replace(/\u00A0/g, ' ');

        // Euro-Zeichen/Entity entfernen (egal wo)
        t = t.replace(/&euro;/gi, '');
        t = t.replace(/€/g, '');

        // Whitespace normalisieren
        t = t.replace(/\s+/g, ' ').trim();

        if (t.length) {
          el.textContent = `${t} €`;
        }
      });
  }

  renderSearchResults(resultsMarkup) {
    console.log('[PredictiveSearch] renderSearchResults', resultsMarkup);
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // NEU: nach dem Rendern Preise umformatieren
    this.formatPredictivePrices();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      this.setLiveRegionText('');
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
